<template>
    <ConfirmationModal
        :title="headerText"
        :progress="saving"
        :allow-close="!saving"
        :yes="confirmLabel"
        no="Cancel"
        @input="onInput"
        @close="onInput(false)"
    >
        <div v-if="entities.length === 1">
            You’re about to reactivate <span class="ActivationConfirmationModal__activationDetails">{{ entities[0].displayName }} ({{ entities[0].contactEmail }})</span>.
        </div>
        <div v-else>
            You’re about to reactivate <span class="ActivationConfirmationModal__activationDetails">{{ entities.length }} entities</span>.
        </div>
        <div v-if="showPauseOption" class="ActivationConfirmationModal__pauseOption">
            <Checkbox
                :model-value="keepPaused"
                aria-describedby="ActivationConfirmationModal__pauseHint"
                @update:model-value="keepPaused = $event"
            >
                Keep verification notifications paused
            </Checkbox>
            <div id="ActivationConfirmationModal__pauseHint" class="ActivationConfirmationModal__pauseHint">
                {{ pauseHint }}
            </div>
        </div>
        <div class="ActivationConfirmationModal__exceptionsWarning">
            {{ exceptionsWarning }}
        </div>
    </ConfirmationModal>
</template>

<script lang="ts">
    import { Component, Prop, Vue } from '@evidentid/vue-property-decorator';

    import { Checkbox } from '@evidentid/dashboard-commons/components/Checkbox';
    import { Entity } from '@evidentid/tprm-portal-lib/models/dashboard';

    import ConfirmationModal from '@/components/confirmation-modal/ConfirmationModal.vue';

    @Component({
        components: {
            Checkbox,
            ConfirmationModal,
        },
        emits: [ 'confirm' ],
    })
    export default class ActivationConfirmationModal extends Vue {
        @Prop({ type: Boolean, default: false })
        private saving!: boolean;

        @Prop({ type: Array, default: () => [] })
        private entities!: Entity[];

        // Controls whether the pause checkbox renders at all: a user without pause access
        // has no way to act on it, so the control (and the patch field it would drive) is
        // withheld rather than shown disabled or defaulted silently.
        @Prop({ type: Boolean, default: true })
        private showPauseOption!: boolean;

        // Checked by default so activating never surprises an insured with outreach unless the
        // admin deliberately opts in.
        private keepPaused: boolean = true;

        private get bulk(): boolean {
            return this.entities.length > 1;
        }

        private get headerText(): string {
            return this.bulk ? 'Activate these entities?' : 'Activate this entity?';
        }

        private get confirmLabel(): string {
            return this.bulk ? 'Activate entities' : 'Activate entity';
        }

        private get pauseHint(): string {
            return this.bulk
                ? 'The entities’ verifications will remain paused after activation, so they won’t receive notifications right away.'
                : 'The entity’s verification will remain paused after activation, so they won’t receive notifications right away.';
        }

        private get exceptionsWarning(): string {
            return this.bulk
                ? 'Note: Any exceptions granted before the entities were deactivated were removed and will not be restored.'
                : 'Note: Any exceptions granted before the entity was deactivated were removed and will not be restored.';
        }

        private onInput(confirmed: boolean): void {
            this.$emit('confirm', { confirmed, keepPaused: this.keepPaused });
        }
    }
</script>
